/**
 * Atri Muni Relatives D24 Calculation Logic
 * Based on Atri Muni system - 24 Horas (sign halves) linked to specific relatives
 *
 * Structure:
 * - 24 Horas: 12 signs × 2 halves = 24 divisions
 * - Each Hora = 15° (half of a sign)
 * - Formula: If degrees < 15° → First Hora, else → Second Hora
 */

import { JatakaResponse, GrahaPosition } from '../../../../../models/api';

export interface PlanetDisplay {
  name: string;
  short_name: string;
  longitude: number;
  is_retrograde: boolean;
  is_lagna?: boolean;
  is_hora?: boolean;
  is_chandra?: boolean;
}

/**
 * 24 Hora types (sign names with 1 or 2)
 */
export const HORA_TYPES = [
  'MESHA_1', 'MESHA_2',
  'VRISHA_1', 'VRISHA_2',
  'MITHUNA_1', 'MITHUNA_2',
  'KARKA_1', 'KARKA_2',
  'SIMHA_1', 'SIMHA_2',
  'KANYA_1', 'KANYA_2',
  'TULA_1', 'TULA_2',
  'ALI_1', 'ALI_2',
  'DHANU_1', 'DHANU_2',
  'MAKARA_1', 'MAKARA_2',
  'KUMBHA_1', 'KUMBHA_2',
  'MINA_1', 'MINA_2'
] as const;

export type HoraType = typeof HORA_TYPES[number];

/**
 * Planetary rulers for Horas (short 2-letter codes)
 */
export type RulerType = 'Ma' | 'Sk' | 'Bu' | 'Ch' | 'Sy' | 'Gu' | 'Sa';

/**
 * Hora descriptions with relative names and planetary rulers
 */
export const HORA_DESCRIPTIONS: Record<HoraType, {
  relative: string;
  ruler: RulerType;
  sign: string;
}> = {
  MESHA_1: {
    relative: "Maternal grandfather",
    ruler: 'Ma',
    sign: 'Mesha'
  },
  MESHA_2: {
    relative: "Paternal grandmother",
    ruler: 'Sk',
    sign: 'Mesha'
  },
  VRISHA_1: {
    relative: "Paternal nieces",
    ruler: 'Bu',
    sign: 'Vrisha'
  },
  VRISHA_2: {
    relative: "Paternal nephews",
    ruler: 'Ch',
    sign: 'Vrisha'
  },
  MITHUNA_1: {
    relative: "Brother",
    ruler: 'Sy',
    sign: 'Mithuna'
  },
  MITHUNA_2: {
    relative: "Sister",
    ruler: 'Bu',
    sign: 'Mithuna'
  },
  KARKA_1: {
    relative: "Mother",
    ruler: 'Sk',
    sign: 'Karka'
  },
  KARKA_2: {
    relative: "Paternal cousin brother",
    ruler: 'Ma',
    sign: 'Karka'
  },
  SIMHA_1: {
    relative: "Son",
    ruler: 'Gu',
    sign: 'Simha'
  },
  SIMHA_2: {
    relative: "Daughter",
    ruler: 'Sa',
    sign: 'Simha'
  },
  KANYA_1: {
    relative: "Maternal uncle",
    ruler: 'Sa',
    sign: 'Kanya'
  },
  KANYA_2: {
    relative: "Maternal aunt",
    ruler: 'Gu',
    sign: 'Kanya'
  },
  TULA_1: {
    relative: "Paternal grandfather",
    ruler: 'Ma',
    sign: 'Tula'
  },
  TULA_2: {
    relative: "Maternal grandmother",
    ruler: 'Sk',
    sign: 'Tula'
  },
  ALI_1: {
    relative: "Maternal nieces",
    ruler: 'Bu',
    sign: 'Ali'
  },
  ALI_2: {
    relative: "Maternal nephews",
    ruler: 'Ch',
    sign: 'Ali'
  },
  DHANU_1: {
    relative: "Grandson",
    ruler: 'Sy',
    sign: 'Dhanu'
  },
  DHANU_2: {
    relative: "Granddaughter",
    ruler: 'Bu',
    sign: 'Dhanu'
  },
  MAKARA_1: {
    relative: "Maternal cousin sister",
    ruler: 'Sk',
    sign: 'Makara'
  },
  MAKARA_2: {
    relative: "Father",
    ruler: 'Ma',
    sign: 'Makara'
  },
  KUMBHA_1: {
    relative: "Maternal cousin brother",
    ruler: 'Gu',
    sign: 'Kumbha'
  },
  KUMBHA_2: {
    relative: "Paternal cousin sister",
    ruler: 'Sa',
    sign: 'Kumbha'
  },
  MINA_1: {
    relative: "Paternal aunt",
    ruler: 'Sa',
    sign: 'Mina'
  },
  MINA_2: {
    relative: "Paternal uncle",
    ruler: 'Gu',
    sign: 'Mina'
  }
};

/**
 * Calculate Hora number from global longitude (0-360)
 */
export function calculateHoraNumber(longitude: number): number {
  // Determine rashi (0-11)
  const rashiIndex = Math.floor(longitude / 30);

  // Determine degree within rashi (0-30)
  const degreeInRashi = longitude % 30;

  // Determine which half (0 = first 15°, 1 = second 15°)
  const horaHalf = degreeInRashi < 15 ? 0 : 1;

  // Hora number: rashi * 2 + horaHalf + 1 (1-24)
  return rashiIndex * 2 + horaHalf + 1;
}

/**
 * Get Hora type from Hora number (1-24)
 */
export function getHoraType(horaNumber: number): HoraType {
  return HORA_TYPES[horaNumber - 1] || HORA_TYPES[0];
}

/**
 * Single planet's Atri Relatives attributes
 */
export interface AtriRelativesAttributes {
  planet: PlanetDisplay;
  horaNumber: number;
  horaType: HoraType;
}

/**
 * Complete Atri Relatives data for all planets
 */
export interface AtriRelativesData {
  divisions: AtriRelativesAttributes[];
}

/**
 * Parse longitude from position string format "rashi|degrees|longitude|speed"
 */
export function parseLongitudeFromPosition(position: string): number {
  if (!position) {
    return 0;
  }

  const parts = position.split('|');
  if (parts.length >= 3) {
    return parseFloat(parts[2]);
  }

  return 0;
}

/**
 * Convert API planet to PlanetDisplay format
 */
export function convertToPlanetDisplay(planet: GrahaPosition, currentHoraPlanet?: string): PlanetDisplay {
  const longitude = parseLongitudeFromPosition(planet.position || '');

  const isHora = currentHoraPlanet && (
    planet.short_name === currentHoraPlanet ||
    planet.name === currentHoraPlanet
  );

  return {
    name: planet.name,
    short_name: planet.short_name,
    longitude: longitude,
    is_retrograde: planet.is_retrograde || false,
    is_lagna: planet.short_name === 'Lg',
    is_hora: isHora || false,
    is_chandra: planet.short_name === 'Ch' || planet.name === 'Chandra'
  };
}

/**
 * Calculate Atri Relatives for all planets
 */
export function calculateAtriRelatives(apiResponse: JatakaResponse): AtriRelativesData {
  const divisions: AtriRelativesAttributes[] = [];

  if (!apiResponse.graha_positions?.planets) {
    return { divisions };
  }

  // Get current hora planet for highlighting
  const currentHoraPlanet = apiResponse.detailed_panchanga?.hora_details?.current_planet;

  apiResponse.graha_positions.planets.forEach(planet => {
    // Convert to PlanetDisplay format
    const planetDisplay = convertToPlanetDisplay(planet, currentHoraPlanet);

    // Calculate Hora number (1-24)
    const horaNumber = calculateHoraNumber(planetDisplay.longitude);

    // Get Hora type
    const horaType = getHoraType(horaNumber);

    divisions.push({
      planet: planetDisplay,
      horaNumber,
      horaType
    });
  });

  return { divisions };
}
